/**
 * FunctionalLandmarkPicker
 *
 * Provides two landmark selection modes for timing and axis goal annotations:
 *
 * - Functional mode: chip buttons grouped by body region (Arm / Leg / Foot)
 *   showing lead/trail variants. The stored name (e.g. "lead_wrist") is
 *   resolved to a concrete anatomical landmark by the backend at evaluation
 *   time based on detected athlete handedness.
 *
 * - Anatomical mode: falls back to the existing "click skeleton" behavior
 *   that lets the coach click a visible landmark on the video overlay.
 *
 * The component defaults to Functional mode because handedness-aware
 * landmarks are the preferred approach for sport movements.
 */

import { useState } from 'react';
import { CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { getLandmarkLabel } from '@/utils/landmarkDetection';
import { FUNCTIONAL_LANDMARK_GROUPS, VIRTUAL_LANDMARK_GROUPS } from '@/types/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FunctionalLandmarkPickerProps {
  /** Currently selected landmark name (functional or anatomical) */
  value: string;
  /** Called when a functional landmark chip is selected */
  onChange: (landmark: string) => void;
  /** Called when user switches to anatomical mode and clicks the skeleton button */
  onClickSkeleton: () => void;
  /** Whether skeleton click mode is available (i.e. parent has registered a callback) */
  canClickSkeleton: boolean;
  /**
   * Visual side identity for accent color.
   * 'A' = cyan (primary landmark), 'B' = amber (secondary landmark).
   */
  side?: 'A' | 'B';
}

type Mode = 'functional' | 'anatomical';

// ─── Helper ──────────────────────────────────────────────────────────────────

function accentClasses(side: 'A' | 'B', selected: boolean) {
  if (side === 'A') {
    return selected
      ? 'bg-cyan-500/20 border-cyan-500/60 text-cyan-300'
      : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:bg-dark-elevated-2 hover:border-cyan-500/30 hover:text-text-primary';
  }
  return selected
    ? 'bg-amber-500/20 border-amber-500/60 text-amber-300'
    : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:bg-dark-elevated-2 hover:border-amber-500/30 hover:text-text-primary';
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FunctionalLandmarkPicker({
  value,
  onChange,
  onClickSkeleton,
  canClickSkeleton,
  side = 'A',
}: FunctionalLandmarkPickerProps) {
  // Default to functional mode — the primary purpose of this component
  const [mode, setMode] = useState<Mode>('functional');

  const isFunctional = value.startsWith('lead_') || value.startsWith('trail_') || value.startsWith('mid_') || value === 'torso_center';
  const dotColor = side === 'A' ? 'bg-cyan-500 ring-cyan-500/30' : 'bg-amber-500 ring-amber-500/30';

  return (
    <div className="space-y-2">
      {/* ── Mode Toggle ───────────────────────────────────────────── */}
      <div className="flex gap-1 p-0.5 rounded-lg bg-dark-elevated-2 w-fit">
        <button
          type="button"
          onClick={() => setMode('functional')}
          className={cn(
            'px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all',
            mode === 'functional'
              ? 'bg-dark-surface text-text-primary shadow-sm'
              : 'text-text-muted hover:text-text-secondary'
          )}
        >
          Functional
        </button>
        <button
          type="button"
          onClick={() => setMode('anatomical')}
          className={cn(
            'px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all',
            mode === 'anatomical'
              ? 'bg-dark-surface text-text-primary shadow-sm'
              : 'text-text-muted hover:text-text-secondary'
          )}
        >
          Anatomical
        </button>
      </div>

      {/* ── Functional Mode: grouped chip grid ────────────────────── */}
      {mode === 'functional' && (
        <div className="space-y-2">
          {Object.entries(FUNCTIONAL_LANDMARK_GROUPS).map(([group, landmarks]) => (
            <div key={group}>
              <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1">
                {group}
              </p>
              <div className="flex flex-wrap gap-1">
                {landmarks.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onChange(name)}
                    className={cn(
                      'px-2 py-1 rounded-md border text-[11px] font-medium transition-all',
                      accentClasses(side, value === name)
                    )}
                  >
                    {getLandmarkLabel(name)}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Computed / virtual landmarks — midpoints and derived body points */}
          <div className="pt-1.5 border-t border-border-subtle/50">
            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1.5">
              Computed Points
            </p>
            <div className="space-y-1.5">
              {Object.entries(VIRTUAL_LANDMARK_GROUPS).map(([group, landmarks]) => (
                <div key={group}>
                  <p className="text-[10px] text-text-muted/70 mb-1">{group}</p>
                  <div className="flex flex-wrap gap-1">
                    {landmarks.map(({ value: lmValue, label }) => (
                      <button
                        key={lmValue}
                        type="button"
                        onClick={() => onChange(lmValue)}
                        className={cn(
                          'px-2 py-1 rounded-md border text-[11px] font-medium transition-all',
                          accentClasses(side, value === lmValue)
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected functional landmark readout */}
          {value && isFunctional && (
            <div
              className={cn(
                'flex items-center gap-2 mt-1 px-2.5 py-1.5 rounded-lg border',
                side === 'A'
                  ? 'bg-cyan-500/10 border-cyan-500/30'
                  : 'bg-amber-500/10 border-amber-500/30'
              )}
            >
              <div className={cn('w-2 h-2 rounded-full ring-2 flex-shrink-0', dotColor)} />
              <span className="text-xs font-medium text-text-primary truncate">
                {getLandmarkLabel(value)}
              </span>
              <span className="text-[10px] text-text-muted ml-auto flex-shrink-0">selected</span>
            </div>
          )}

          {/* Hint when nothing is selected */}
          {!value && (
            <p className="text-[11px] text-text-muted">
              Select a body part above. Lead/Trail resolve at evaluation time based on athlete handedness.
            </p>
          )}
        </div>
      )}

      {/* ── Anatomical Mode: skeleton click button ────────────────── */}
      {mode === 'anatomical' && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={onClickSkeleton}
            disabled={!canClickSkeleton}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all text-sm',
              value && !isFunctional
                ? cn(
                    'border-opacity-40 font-medium',
                    side === 'A'
                      ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300'
                      : 'bg-amber-500/10 border-amber-500/40 text-amber-300'
                  )
                : 'bg-dark-elevated-1 border-dashed border-border-subtle text-text-secondary hover:border-cyan-500/40 hover:bg-cyan-500/5',
              !canClickSkeleton && 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              {value && !isFunctional ? (
                <>
                  <div className={cn('w-2.5 h-2.5 rounded-full ring-2 flex-shrink-0', dotColor)} />
                  <span className="truncate">{getLandmarkLabel(value)}</span>
                </>
              ) : (
                <>
                  <CursorArrowRaysIcon className="h-4 w-4 flex-shrink-0" />
                  <span>Click skeleton to select</span>
                </>
              )}
            </div>
            {value && !isFunctional && (
              <span className="text-[10px] opacity-60 flex-shrink-0 ml-2">Change</span>
            )}
          </button>

          <p className="text-[11px] text-text-muted">
            Click a landmark on the skeleton overlay to select a specific anatomical point.
          </p>
        </div>
      )}
    </div>
  );
}
